import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  useDefaultEditorStyle,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActionButton } from '@fluentui/react/lib/Button';
import { useI18n, useSiteConfig } from '@bingads-webui-campaign-react/smart-page-context';

import { AddProfessionalServiceDialog } from './components/add-professional-service-dialog';
import { proxyReadProfessionalServicesComponentConfig } from './component-config-reader';
import { covertConfigToModel } from './data-config';
import { SmartProfessionalServiceEditorItemList } from './components/professional-service-editor-item-list';

const componentKey = WEBSITE_SOURCE_DATA_KEY_MAPPING.Services;
const MaxCount = 12;

export const UnStyledSmartProfessionalServiceEditor = ({
  classes,
}) => {
  const i18n = useI18n();
  const [showDialog, setShowDialog] = useState(false);
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const [editingIndex, setEditingIndex] = useState(-1);

  const {
    titleClassName,
    descriptionClassName,
  } = useDefaultEditorStyle();

  const services = proxyReadProfessionalServicesComponentConfig(siteConfig);

  const toggleDialog = useCallback((show) => {
    setShowDialog(show);
    if (!show) {
      setEditingIndex(-1);
    }
  }, []);

  const updateServices = useCallback((newServices) => {
    dispatchSiteConfig({
      type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
      payload: {
        name: componentKey,
        value: newServices.map(covertConfigToModel),
      },
    });
  }, [dispatchSiteConfig]);

  const onAdd = useCallback((newService) => {
    const newServices = [...services];

    if (editingIndex > -1) {
      newServices[editingIndex] = newService;
    } else {
      newServices.push(newService);
    }

    updateServices(newServices);
    toggleDialog(false);
  }, [updateServices, services, toggleDialog, editingIndex]);

  const onDelete = useCallback((index) => {
    const newServices = [...services];
    newServices.splice(index, 1);
    updateServices(newServices);
  }, [services, updateServices]);

  const onEdit = useCallback((index) => {
    setEditingIndex(index);
    toggleDialog(true);
  }, [toggleDialog]);

  return (
    <div className="smart-page-professional-service-editor-container">
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Services'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('This section is where you\'ll display the different services your business offers (maximum 12 services).'))}
      </div>
      <div className="smart-page-professional-service-list">
        <div className={classes.listLabelContainer}>
          <ActionButton
            className={classnames(classes.addBtn, 'add-professional-service-button')}
            onClick={() => toggleDialog(true)}
            disabled={services && services.length >= MaxCount}
          >
            {i18n.getString(_TL_('+ Add service'))}
          </ActionButton>
        </div>
        <SmartProfessionalServiceEditorItemList
          onEdit={onEdit}
          onDelete={onDelete}
          services={services}
        />
      </div>
      {
        showDialog &&
        <AddProfessionalServiceDialog
          i18n={i18n}
          onAdd={onAdd}
          onCancel={() => toggleDialog(false)}
          services={services}
          editingIndex={editingIndex}
        />
      }
    </div>
  );
};

UnStyledSmartProfessionalServiceEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
};

export const SmartProfessionalServiceEditor = withDefaultStyles(
  UnStyledSmartProfessionalServiceEditor,
  () => ({
    listLabelContainer: {
      display: 'flex',
    },
    addBtn: {
      marginLeft: 'auto',
      marginRight: '12px',
      height: '25px',
      '&:enabled': {
        color: '#0063B1',
      },
    },
  })
);
